import { createContext, Dispatch, ReactNode, useContext, useReducer } from "react";
import { Devices, Company, Config, Device, Template, UnitDetails, ConfigHistory } from "./types";
import { cloneDeep, isEqual } from "lodash-es";

export const initDevice = (unitid: number): Device => ({
  unitid,
  unitDetails: null,
  company: null,
  template: null,
  accountinfo: null,
  config: null,
  configHistory: null,
  noConfig: false,
  currentValue: null,
  prevValue: null,
  unknownDevice: null,
  primaryUnitid: null,
  saving: false,
  syncLoading: false,
});

// REDUCER

export enum DevicesReducerTypes {
  CLEAR = "CLEAR",
  SET_DEVICE_LIST = "SET_DEVICE_LIST",
  SET_DEVICE = "SET_DEVICE",
  CREATE_DEVICE = "CREATE_DEVICE",
  CLEAR_DEVICE = "CLEAR_DEVICE",
  SET_UNIT_DETAILS = "SET_UNIT_DETAILS",
  SET_COMPANY = "SET_COMPANY",
  SET_TEMPLATE = "SET_TEMPLATE",
  SET_CONFIG = "SET_CONFIG",
  SET_CONFIG_HISTORY = "SET_CONFIG_HISTORY",
  SET_CURRENT_VALUE = "SET_CURRENT_VALUE",
  SET_LOCAL_VALUE = "SET_LOCAL_VALUE",
  SET_EMPTY_CONFIG = "SET_EMPTY_CONFIG",
}

export type DevicesReducerProps =
  | { type: DevicesReducerTypes.SET_DEVICE_LIST; payload: { devices: Device[] } }
  | { type: DevicesReducerTypes.SET_DEVICE; payload: { unitid: number; device: Device | null } }
  | { type: DevicesReducerTypes.CREATE_DEVICE; payload: { unitid: number } }
  | { type: DevicesReducerTypes.CLEAR_DEVICE; payload: { unitid: number } }
  | { type: DevicesReducerTypes.SET_UNIT_DETAILS; payload: { unitid: number; unitDetails: UnitDetails | null; primaryUnitid?: number } }
  | { type: DevicesReducerTypes.SET_COMPANY; payload: { unitid: number; company: Company | null } }
  | { type: DevicesReducerTypes.SET_TEMPLATE; payload: { unitid: number; template: Template | null } }
  | { type: DevicesReducerTypes.SET_CONFIG; payload: { unitid: number; config: Config | null; force?: boolean } }
  | { type: DevicesReducerTypes.SET_CONFIG_HISTORY; payload: { unitid: number; configHistory: ConfigHistory[] } }
  | { type: DevicesReducerTypes.SET_CURRENT_VALUE; payload: { unitid: number; currentValue: any } }
  | { type: DevicesReducerTypes.SET_LOCAL_VALUE; payload: { unitid: number; saving?: boolean; syncLoading?: boolean } }
  | { type: DevicesReducerTypes.SET_EMPTY_CONFIG; payload: { unitid: number; key: string; emptyConfig: any } };

const DevicesReducer = (state: Devices, { type, payload: p }: DevicesReducerProps) => {
  let payload, device;
  switch (type) {
    case DevicesReducerTypes.SET_DEVICE_LIST:
      payload = p as { devices: Device[] };
      state.deviceList = payload.devices;
      state.devices.clear();
      payload.devices.forEach((device) => state.devices.set(device.unitid, device));
      return { ...state };
    case DevicesReducerTypes.CREATE_DEVICE:
      payload = p as { unitid: number };
      device = initDevice(payload.unitid);
      state.devices.set(device.unitid, device);
      state.deviceList.push(device);
      return { ...state };
    case DevicesReducerTypes.SET_UNIT_DETAILS:
      payload = p as { unitid: number; unitDetails: UnitDetails | null; primaryUnitid?: number };
      device = state.devices.get(payload.unitid) as Device;
      if (payload.unitDetails && isEqual(device.unitDetails, payload.unitDetails)) {
        return state;
      }
      if (!payload.unitDetails) {
        state.devices.set(payload.unitid, { ...initDevice(payload.unitid), unknownDevice: payload.primaryUnitid === undefined, primaryUnitid: payload.primaryUnitid });
        state.deviceList = Array.from(state.devices.values());
      } else {
        device.unitDetails = payload.unitDetails;
        device.unknownDevice = false;
      }
      return { ...state };
    case DevicesReducerTypes.SET_TEMPLATE:
      payload = p as { unitid: number; template: Template | null };
      device = state.devices.get(payload.unitid) as Device;
      if (payload.template && isEqual(device.template, payload.template)) {
        return state;
      }
      device.template = payload.template;
      return { ...state };
    case DevicesReducerTypes.SET_CONFIG:
      payload = p as { unitid: number; config: Config | null; force: boolean };
      device = state.devices.get(payload.unitid) as Device;
      if (payload.config && device?.currentValue && isEqual(device.config, payload.config)) {
        return state;
      }
      device.noConfig = !payload.config?.toolboxConfig;
      device.config = payload.config;
      if (payload.config && (device.currentValue === null || payload.force)) {
        device.currentValue = cloneDeep(payload.config.toolboxConfig);
        device.prevValue = cloneDeep(payload.config.toolboxConfig);
      }
      return { ...state };
    case DevicesReducerTypes.SET_CONFIG_HISTORY:
      payload = p as { unitid: number; configHistory: ConfigHistory[] };
      device = state.devices.get(payload.unitid) as Device;
      if (isEqual(device.configHistory, payload.configHistory)) {
        return state;
      }
      device.configHistory = payload.configHistory;
      return { ...state };
    case DevicesReducerTypes.SET_CURRENT_VALUE:
      payload = p as { unitid: number; currentValue: any };
      device = state.devices.get(payload.unitid) as Device;
      const currentValue = payload.currentValue instanceof Function ? payload.currentValue(cloneDeep(device.prevValue)) : cloneDeep(payload.currentValue);
      if (!device || (currentValue && isEqual(device.prevValue, currentValue))) {
        return state;
      }
      if (currentValue.configVersion === undefined) {
        currentValue.configVersion = device.currentValue.configVersion;
      }
      device.currentValue = currentValue;
      device.prevValue = cloneDeep(currentValue);
      return { ...state };
    case DevicesReducerTypes.SET_LOCAL_VALUE:
      payload = p as { unitid: number; saving?: boolean; syncLoading?: boolean };
      device = state.devices.get(payload.unitid) as Device;
      let changed;
      if (payload.saving !== undefined && device?.saving !== payload.saving) {
        device.saving = payload.saving;
        changed = true;
      }
      if (payload.syncLoading !== undefined && device.syncLoading !== payload.syncLoading) {
        device.syncLoading = payload.syncLoading;
        changed = true;
      }
      return changed ? { ...state } : state;
    case DevicesReducerTypes.SET_EMPTY_CONFIG:
      payload = p as { unitid: number; key: string; emptyConfig: any };
      device = state.devices.get(payload.unitid) as Device;
      device.noConfig = false;
      if (payload.emptyConfig && device.currentValue?.[payload.key] && isEqual(device.config?.toolboxConfig?.[payload.key], payload.emptyConfig)) {
        return state;
      }
      if (!device.config) {
        device.config = {
          toolboxConfigOrig: null,
          toolboxConfig: {
            templateId: undefined,
            configVersion: undefined,
            [payload.key]: payload.emptyConfig,
          },
        } as Config;
        device.currentValue = cloneDeep(device.config.toolboxConfig);
        device.prevValue = cloneDeep(device.config.toolboxConfig);
      } else {
        if (!device.config.toolboxConfigOrig) {
          device.config.toolboxConfigOrig = cloneDeep(device.config.toolboxConfig);
        }
        device.config.toolboxConfig[payload.key] = payload.emptyConfig;
        device.currentValue[payload.key] = cloneDeep(payload.emptyConfig);
        device.prevValue[payload.key] = cloneDeep(payload.emptyConfig);
      }
      return { ...state };
  }
  return state;
};

// CONTEXT

const initialState = {
  devices: new Map<number, Device>(),
  deviceList: [],
};

export const clearDevices = () => {
  initialState.devices.clear();
  initialState.deviceList.splice(0, initialState.deviceList.length);
};

const DevicesContext = createContext<{
  state: Devices;
  dispatch: Dispatch<any>;
}>({
  state: initialState,
  dispatch: () => {},
});

export const DevicesProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(DevicesReducer, initialState);

  return <DevicesContext.Provider value={{ state, dispatch }}>{children}</DevicesContext.Provider>;
};

export const useDevices = () => {
  const devicesState = useContext(DevicesContext);

  if (devicesState === null) {
    throw new Error("useDevices must be used within an DevicesProvider");
  }

  return devicesState;
};
